import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/common/Pagination';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useTableZoom } from '@/lib/hooks/useTableZoom';
import { useAuth } from '@/features/auth';
import { useSettingsFilters } from '../hooks';
import { paymentMethodsApi, settingsQueryKeys } from '../api';
import { SettingsDataTable, SettingsAction, useSettingsColumns } from '../components';
import type { PaymentMethod } from '../types';

export function PaymentMethodsPage() {
  const { t } = useTranslation();
  const { hasAccess } = useAuth();
  const { filters, updateFilter, setPage, setLimit, handleSort } = useSettingsFilters();

  // Zoom is owned here so its control can sit in the Pagination row (left of
  // the per-page select) while the table applies the same value to its rows.
  const tableZoom = useTableZoom();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: settingsQueryKeys.paymentMethods.list(filters),
    queryFn: () => paymentMethodsApi.getAll(filters),
  });

  const columns = useSettingsColumns<PaymentMethod>({
    orderBy: filters.orderBy,
    order: filters.order,
    onSort: handleSort,
    resource: 'payment-method',
    apiEndpoint: 'Payment-Methods',
    queryKey: 'paymentMethods',
    title: t('paymentMethod'),
  });

  return (
    <div>
      <div className="flex py-2 px-4 justify-between items-center border-b">
        <h1 className="text-xl font-semibold">{t('paymentMethod')}</h1>
        <div className="flex items-center gap-3">
          {hasAccess('payment', 'get') && (
            <Link to="/app/payments">
              <Button variant="outline">{t('payments')}</Button>
            </Link>
          )}
          {hasAccess('payment-method', 'create') && (
            <SettingsAction
              title={t('paymentMethod')}
              apiEndpoint="Payment-Methods"
              queryKey="paymentMethods"
              placeholder={t('enterPaymentMethodName') || t('enterName')}
              successMessageCreate={t('paymentMethodCreated')}
              successMessageUpdate={t('paymentMethodUpdated')}
            />
          )}
        </div>
      </div>

      <div className="px-4 pt-4">
        <SettingsDataTable
          tableZoom={tableZoom}
          tableId="payment-methods"
          data={data?.results || []}
          columns={columns}
          filters={filters}
          updateFilter={updateFilter}
          isLoading={isLoading}
          isError={isError}
          error={error?.message}
          emptyMessage={t('noPaymentMethods')}
          createButton={
            hasAccess('payment-method', 'create') ? (
              <SettingsAction
                title={t('paymentMethod')}
                apiEndpoint="Payment-Methods"
                queryKey="paymentMethods"
              />
            ) : undefined
          }
        />

        <Pagination
          leadingControls={<TableZoomControls zoom={tableZoom} />}
          page={filters.page}
          limit={filters.limit}
          total={data?.meta?.total ?? 0}
          onPageChange={setPage}
          onLimitChange={setLimit}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
